import * as React from 'react';
import { core } from '@tock/coretypes';

const preventBubbleOutside = (event: React.SyntheticEvent) => {
  if (!event) {
    return;
  }
  event.stopPropagation();
  if (event.nativeEvent) {
    event.nativeEvent.stopImmediatePropagation();
  }
};

/**
 * Utility method for sorting event ticket types.
 * Event ticket types should be sorted in ascending order based on the date of their next available offering.
 * @param eventTickets : Event ticket types to be sorted
 * @returns : Sorted event ticket types array.
 */
const sortComputedEventTickets = (
  eventTickets: Array<core.TicketType>,
  eventTemplates: Record<number, core.EventTemplate[]>
) => {
  const nextOfferingMilliMap: Map<core.TicketType, number> = eventTickets.reduce(
    (map, ticketType) => {
      // If we can pull a date out of the event templates, that takes precedence.
      const nextDates = (eventTemplates[ticketType.id] ?? [])
        .map((template) => template.nextDate)
        .filter((date) => date && date !== '')
        .sort();
      if (nextDates.length > 0) {
        return map.set(ticketType, Date.parse(nextDates[0]));
      }

      // If we can't pull a date out of the event templates, fall back to the
      // old behavior of looking at ticket group summaries.
      const availableOfferings = ticketType.ticketGroupSummary.filter(
        (ticketGroupSummary) =>
          ticketGroupSummary.dateTime && Date.parse(ticketGroupSummary.dateTime) > Date.now()
      );
      if (availableOfferings.length > 0) {
        const earliestOffering = availableOfferings[0];
        const nextOfferingMilli = earliestOffering.dateTime
          ? Date.parse(earliestOffering.dateTime)
          : Number.MAX_SAFE_INTEGER;
        map.set(ticketType, nextOfferingMilli);
      }
      return map;
    },
    new Map()
  );

  const sortedOfferings = [...eventTickets].sort((a, b) => {
    return (
      (nextOfferingMilliMap.get(a) ?? Number.MAX_SAFE_INTEGER) -
      (nextOfferingMilliMap.get(b) ?? Number.MAX_SAFE_INTEGER)
    );
  });

  return sortedOfferings;
};

export { preventBubbleOutside, sortComputedEventTickets };
